// User actions API functions
// This file contains user-related API functions for tracking user behavior

import { getFirebaseDb } from "./firebase-ultra-safe";
import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit } from "firebase/firestore";

export interface UserAction {
  id?: string;
  userId: string;
  action: string;
  actionType: "view" | "purchase" | "chat" | "login" | "search" | "upload" | "edit" | "delete" | "other";
  page?: string;
  productId?: string;
  productTitle?: string;
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  deviceType?: "desktop" | "mobile" | "tablet";
  location?: string;
  timestamp?: any;
}

/**
 * 비회원 세션 ID 생성 (IP + User Agent 기반)
 */
function generateAnonymousSessionId(ipAddress?: string, userAgent?: string): string {
  if (typeof window === "undefined") {
    // 서버 사이드에서는 IP 기반으로만 생성
    const hash = ipAddress || "unknown";
    return `anonymous_${hash.substring(0, 16)}`;
  }

  // 클라이언트 사이드: localStorage에 세션 ID 저장 (없으면 생성)
  const storageKey = "tone_market_anonymous_session";
  let sessionId: string | null = null;
  
  try {
    sessionId = localStorage.getItem(storageKey);
  } catch (e) {
    // localStorage 접근 실패 (예: 사파리 시크릿 모드, 일부 모바일 브라우저)
    console.debug("localStorage 접근 실패, 세션 ID 새로 생성:", e);
  }

  if (!sessionId) {
    // IP + User Agent 기반 해시 생성
    const userAgentStr = userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : "unknown");
    const data = `${ipAddress || "unknown"}_${userAgentStr}`;
    // 간단한 해시 함수
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    sessionId = `anonymous_${Math.abs(hash).toString(36)}_${Date.now().toString(36)}`;
    
    try {
      localStorage.setItem(storageKey, sessionId);
    } catch (e) {
      // localStorage 저장 실패해도 세션 ID는 반환 (메모리에서만 사용)
      console.debug("localStorage 저장 실패, 메모리에서만 사용:", e);
    }
  }

  return sessionId;
}

/**
 * 회원 행동 로그 기록 (비회원도 추적 가능)
 */
export const createUserAction = async (action: Omit<UserAction, "id" | "timestamp">): Promise<{ success: boolean; error?: string }> => {
  try {
    const db = getFirebaseDb();
    if (!db) {
      console.error("❌ DB 초기화 실패 - 행동 로그 기록 불가");
      return { success: false, error: "DB 초기화 실패" };
    }

    // 비회원인 경우 세션 ID 생성
    let userId = action.userId;
    if (!userId || userId === "null" || userId === "undefined") {
      userId = generateAnonymousSessionId(action.ipAddress, action.userAgent);
    }

    // 디바이스 타입 자동 감지
    const deviceType = getDeviceType(action.userAgent);

    // undefined 필드는 제외 (Firestore는 undefined를 허용하지 않음)
    // metadata에서 undefined 값 제거
    const cleanMetadata: Record<string, any> = {};
    if (action.metadata) {
      Object.keys(action.metadata).forEach(key => {
        if (action.metadata![key] !== undefined) {
          cleanMetadata[key] = action.metadata![key];
        }
      });
    }
    
    const actionDoc: Record<string, any> = {
      userId: userId,
      isAnonymous: !action.userId || action.userId === "null" || action.userId === "undefined",
      action: action.action,
      actionType: action.actionType,
      metadata: cleanMetadata,
      deviceType: deviceType,
      timestamp: serverTimestamp(),
    };

    // undefined가 아닌 필드만 추가
    if (action.page !== undefined) actionDoc.page = action.page;
    if (action.productId !== undefined) actionDoc.productId = action.productId;
    if (action.productTitle !== undefined) actionDoc.productTitle = action.productTitle;
    if (action.ipAddress !== undefined) actionDoc.ipAddress = action.ipAddress;
    if (action.userAgent !== undefined) actionDoc.userAgent = action.userAgent;
    if (action.location !== undefined) actionDoc.location = action.location;

    await addDoc(collection(db, "user_actions"), actionDoc);
    
    return { success: true };
  } catch (error) {
    console.error("❌ 행동 로그 기록 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "행동 로그 기록 실패",
    };
  }
};

/**
 * 특정 회원의 행동 로그 조회
 */
export const getUserActions = async (
  userId: string,
  limitCount: number = 100
): Promise<{ success: boolean; actions?: UserAction[]; error?: string }> => {
  try {
    const db = getFirebaseDb();
    if (!db) {
      return { success: false, error: "DB 초기화 실패" };
    }

    const q = query(
      collection(db, "user_actions"),
      where("userId", "==", userId),
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const actions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp,
    })) as UserAction[];

    return { success: true, actions };
  } catch (error) {
    console.error("❌ 행동 로그 조회 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "행동 로그 조회 실패",
    };
  }
};

/**
 * 모든 행동 로그 조회 (관리자용)
 */
export const getAllUserActions = async (
  limitCount: number = 500
): Promise<{ success: boolean; actions?: UserAction[]; error?: string }> => {
  try {
    const db = getFirebaseDb();
    if (!db) {
      return { success: false, error: "DB 초기화 실패" };
    }

    const q = query(
      collection(db, "user_actions"),
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const actions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp,
    })) as UserAction[];

    return { success: true, actions };
  } catch (error) {
    console.error("❌ 행동 로그 조회 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "행동 로그 조회 실패",
    };
  }
};

/**
 * 행동 로그 삭제 (관리자용)
 */
export const deleteUserAction = async (actionId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const db = getFirebaseDb();
    if (!db) {
      return { success: false, error: "DB 초기화 실패" };
    }

    const { doc, deleteDoc } = await import("firebase/firestore");
    await deleteDoc(doc(db, "user_actions", actionId));

    return { success: true };
  } catch (error) {
    console.error("❌ 행동 로그 삭제 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "행동 로그 삭제 실패",
    };
  }
};

/**
 * 인증 관련 행동 로그 기록
 */
export const logAuthAction = async (
  action: string,
  userId?: string,
  metadata?: Record<string, any>
): Promise<void> => {
  if (!userId) return;

  try {
    await createUserAction({
      userId,
      action,
      actionType: "login",
      metadata: {
        ...metadata,
        authAction: true,
      },
    });
  } catch (error) {
    console.error("인증 행동 로그 기록 실패:", error);
  }
};

/**
 * 관리자 액션 로그 기록 (기존 함수와 호환성 유지)
 */
export const logAdminAction = async (
  adminUid: string,
  action: string,
  targetId?: string,
  metadata?: Record<string, any>
): Promise<void> => {
  try {
    await createUserAction({
      userId: adminUid,
      action: `[관리자] ${action}`,
      actionType: "other",
      metadata: {
        ...metadata,
        isAdminAction: true,
        targetId,
      },
    });
  } catch (error) {
    console.error("관리자 액션 로그 기록 실패:", error);
  }
};

/**
 * User Agent에서 디바이스 타입 감지
 */
function getDeviceType(userAgent?: string): "desktop" | "mobile" | "tablet" {
  if (!userAgent) return "desktop";
  const ua = userAgent.toLowerCase();
  if (ua.includes("mobile") && !ua.includes("tablet")) return "mobile";
  if (ua.includes("tablet") || ua.includes("ipad")) return "tablet";
  return "desktop";
}
